// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Edit, Trash2, CheckCircle, XCircle } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal } from '@/components/ui'
import { Input } from '@/components/ui'
import {
  useJournalEntry,
  useDeleteJournalEntry,
  usePostJournalEntry,
  useVoidJournalEntry,
} from '@/hooks/useAccounting'
import { formatBaht } from '@/lib/money'
import { useDateFormatter } from '@/hooks/useDateFormatter'

const REFERENCE_ROUTES: Record<string, string> = {
  Invoice: '/invoices',
  Payment: '/payments',
  Expense: '/expenses',
}

export function JournalEntryDetailPage() {
  const { t } = useTranslation('accounting')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const entryId = Number(id)
  const navigate = useNavigate()
  const formatDate = useDateFormatter()

  const { data: entry, isLoading, isFetching } = useJournalEntry(entryId)
  const deleteEntry = useDeleteJournalEntry()
  const postEntry = usePostJournalEntry()
  const voidEntry = useVoidJournalEntry()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showPostModal, setShowPostModal] = useState(false)
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [voidReason, setVoidReason] = useState('')

  const handleDelete = async () => {
    await deleteEntry.mutateAsync(entryId)
    navigate('/accounting/journal-entries')
  }

  const handlePost = async () => {
    await postEntry.mutateAsync(entryId)
    setShowPostModal(false)
  }

  const handleVoid = async () => {
    if (!voidReason.trim()) return
    await voidEntry.mutateAsync({ id: entryId, reason: voidReason })
    setShowVoidModal(false)
    setVoidReason('')
  }

  const getStatusBadge = () => {
    if (!entry) return null
    switch (entry.status) {
      case 'posted':
        return <Badge variant="success">{t('statuses.posted')}</Badge>
      case 'voided':
        return <Badge variant="ghost">{t('statuses.voided')}</Badge>
      default:
        return <Badge variant="warning">{t('statuses.draft')}</Badge>
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('journal_entries.title'), href: '/accounting/journal-entries' },
            { label: '...' },
          ]}
        />
        <Card>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  // T139-002b — /accounting/journal-entries/:id for a missing id used to
  // return null here, rendering a completely blank page (0 characters in
  // <main>, no message, no way back). Every sibling detail page (invoices,
  // customers, leases) handles this; copied the invoices shape.
  if (!entry) {
    return (
      <Card>
        <EmptyState
          title={t('journal_entries.not_found')}
          action={<Button onClick={() => navigate('/accounting/journal-entries')}>{t('journal_entries.back_to_journal_entries')}</Button>}
        />
      </Card>
    )
  }

  // debit/credit arrive as strings (BigDecimal -> JSON); coerce before summing so
  // the reduce adds numbers instead of string-concatenating (was -> formatBaht NaN).
  const totalDebit = entry.journal_lines.reduce((sum, l) => sum + (Number(l.debit) || 0), 0)
  const totalCredit = entry.journal_lines.reduce((sum, l) => sum + (Number(l.credit) || 0), 0)
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01

  const referenceTypeLabels: Record<string, string> = {
    Invoice: t('journal_entries.reference_types.invoice'),
    Payment: t('journal_entries.reference_types.payment'),
    Expense: t('journal_entries.reference_types.expense'),
    StripePayout: t('journal_entries.reference_types.stripe_payout'),
    // A late fee's own entry (JE-LATEFEE) points at the invoice line. It has
    // no page of its own, so it is labelled and not linked.
    InvoiceItem: t('journal_entries.reference_types.late_fee_line'),
  }
  const referenceTypeLabel = entry.reference_type
    ? referenceTypeLabels[entry.reference_type] ?? entry.reference_type
    : ''
  const referenceRoute = entry.reference_type ? REFERENCE_ROUTES[entry.reference_type] : undefined

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('journal_entries.title'), href: '/accounting/journal-entries' },
          { label: entry.entry_number },
        ]}
      />

      <PageHeader
        title={entry.entry_number}
        description={entry.description}
        isRefreshing={isFetching && !isLoading}
        // EP-106 Item 1 — primary is state-swapped: Post while draft, Void
        // once posted; absent once voided (Delete, draft-only, promotes then).
        primaryAction={
          entry.status === 'draft'
            ? {
                key: 'post',
                label: t('journal_entries.post_entry'),
                icon: CheckCircle,
                onClick: () => setShowPostModal(true),
              }
            : entry.status === 'posted'
              ? {
                  key: 'void',
                  label: t('journal_entries.void_entry'),
                  icon: XCircle,
                  onClick: () => setShowVoidModal(true),
                }
              : undefined
        }
        overflowActions={[
          {
            key: 'edit',
            label: tc('actions.edit'),
            icon: Edit,
            to: `/accounting/journal-entries/${entryId}/edit`,
            hidden: entry.status !== 'draft',
          },
          {
            key: 'delete',
            label: tc('actions.delete'),
            icon: Trash2,
            onClick: () => setShowDeleteModal(true),
            destructive: true,
            hidden: entry.status !== 'draft',
          },
        ]}
      />

      {/* Entry Header */}
      <Card>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className="text-sm text-base-content/60">{t('labels.entry_number')}</p>
            <p className="font-mono font-semibold">{entry.entry_number}</p>
          </div>
          <div>
            <p className="text-sm text-base-content/60">{t('labels.entry_date')}</p>
            <p>{formatDate(entry.entry_date)}</p>
          </div>
          <div>
            <p className="text-sm text-base-content/60">{t('labels.status')}</p>
            {getStatusBadge()}
          </div>
          {entry.property && (
            <div>
              <p className="text-sm text-base-content/60">{t('labels.property')}</p>
              <p>{entry.property.name}</p>
            </div>
          )}
        </div>

        {entry.void_reason && (
          <div className="mt-4 p-3 bg-error/10 rounded-lg">
            <p className="text-sm font-medium text-error">{t('journal_entries.void_reason')}</p>
            <p className="text-sm">{entry.void_reason}</p>
          </div>
        )}

        {entry.reference_type && (
          <div className="mt-4">
            <p className="text-sm text-base-content/60">{t('labels.reference')}</p>
            {referenceRoute ? (
              <Link
                to={`${referenceRoute}/${entry.reference_id}`}
                className="link link-primary text-sm"
              >
                {referenceTypeLabel} #{entry.reference_id}
              </Link>
            ) : (
              <p className="text-sm">
                {referenceTypeLabel} #{entry.reference_id}
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Journal Lines */}
      <Card>
        <div className="overflow-x-auto">
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th>{t('labels.account')}</th>
                <th>{t('labels.description')}</th>
                <th className="text-right">{t('labels.debit')}</th>
                <th className="text-right">{t('labels.credit')}</th>
              </tr>
            </thead>
            <tbody>
              {entry.journal_lines.map((line, idx) => (
                <tr key={line.id ?? idx} className="hover">
                  <td>
                    {line.gl_account_code ? (
                      <span className="font-mono text-sm">
                        {line.gl_account_code} — {line.gl_account_name}
                      </span>
                    ) : (
                      <span className="text-base-content/40">—</span>
                    )}
                  </td>
                  <td className="text-sm text-base-content/70">{line.description || '—'}</td>
                  <td className="text-right tabular-nums">
                    {Number(line.debit) > 0 ? formatBaht(Number(line.debit), { locale: 'en', decimals: 2 }) : '—'}
                  </td>
                  <td className="text-right tabular-nums">
                    {Number(line.credit) > 0 ? formatBaht(Number(line.credit), { locale: 'en', decimals: 2 }) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold border-t-2">
                <td colSpan={2}>{tc('labels.total')}</td>
                <td className="text-right tabular-nums">{totalDebit ? formatBaht(totalDebit, { locale: 'en', decimals: 2 }) : '—'}</td>
                <td className="text-right tabular-nums">{totalCredit ? formatBaht(totalCredit, { locale: 'en', decimals: 2 }) : '—'}</td>
              </tr>
              {!isBalanced && (
                <tr>
                  <td colSpan={4}>
                    <span className="text-sm text-error">{t('messages.not_balanced')}</span>
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </Card>

      {/* Post Confirm Modal */}
      <Modal
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
        title={t('journal_entries.post_entry')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowPostModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button variant="success" onClick={handlePost} loading={postEntry.isPending}>
              {t('journal_entries.post_entry')}
            </Button>
          </div>
        }
      >
        <p>{t('journal_entries.post_confirm')}</p>
      </Modal>

      {/* Void Modal */}
      <Modal
        isOpen={showVoidModal}
        onClose={() => {
          setShowVoidModal(false)
          setVoidReason('')
        }}
        title={t('journal_entries.void_entry')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              onClick={() => {
                setShowVoidModal(false)
                setVoidReason('')
              }}
            >
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="error"
              onClick={handleVoid}
              loading={voidEntry.isPending}
              disabled={!voidReason.trim()}
            >
              {t('journal_entries.void_entry')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p>{t('journal_entries.void_confirm')}</p>
          <Input
            label={t('journal_entries.void_reason')}
            placeholder={t('journal_entries.void_reason_placeholder')}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            required
          />
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={tc('actions.delete')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button variant="error" onClick={handleDelete} loading={deleteEntry.isPending}>
              {tc('actions.delete')}
            </Button>
          </div>
        }
      >
        <p>{t('journal_entries.delete_confirm')}</p>
      </Modal>
    </div>
  )
}
